import React, { useEffect, useState } from "react";
import { Mic, MicOff, Video, VideoOff } from "lucide-react";
import BottomInterviewButton from "./BottomInterviewButton";
import { MicOpts } from "@/utils/types";
import { Dispatch, FC, SetStateAction } from "react";
import { useAppContext } from "@/appContext";
import { stopRecording } from "@/livekitApi";
import CustomTooltip from "./CustomTooltip";
import clsx from "clsx"; // Ensure clsx is imported
import { cn } from "@/utils/utils";
import { SubmitAssessment, UpdateAssessmentNextQuestion } from "@/api";

interface Props {
  micOpts?: MicOpts;
  setShowLoader?: Dispatch<SetStateAction<boolean>>;
  recognizer?: any;
  nextClick?: () => void;
}

const InterviewButtons: FC<Props> = ({
  micOpts,
  setShowLoader,
  recognizer,
  nextClick,
}) => {
  const { reducers, getFeedbackAsync, stopAudio, state, navigateThenRun } = useAppContext();
  const { setshowFeedback, setshowNextConfirmation } = reducers;

  // Add local state for practice tooltip flow
  const [practiceStep, setPracticeStep] = useState(state.practiceStep);

  // Get token from query param if it exists
  const [token, setToken] = useState<string | null>(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("candidate");
    if (t) setToken(t);
  }, []);

  const handleMicClick = () => {
    if (!micOpts) return;
    if (!micOpts.micListening) {
      micOpts.sttFromMic();
      if (state.canStop) {
        stopAudio();
      }
      // Practice flow: advance to next step if on mic tooltip step 1
      if (state.isPractice && practiceStep === 1) setPracticeStep(2);
    }
  };

  useEffect(() => {
    // Update practiceStep state when state.practiceStep changes
    setPracticeStep(state.practiceStep);
  }, [state.practiceStep]);

  const handleSubmit = () => {
    if (!micOpts) return;
    micOpts.stopMic();
    // Practice flow: advance to next step if on mic tooltip step 2
    if (state.isPractice && practiceStep === 2) setPracticeStep(3);
  };

  const handleVideoClick = () => {
    reducers.setshowVideo(!state.showVideo);
    // Practice flow: advance to next step if on video tooltip step 0
    if (state.isPractice && practiceStep === 0) setPracticeStep(1);
  };

  const handleFinish = async () => {
    // Practice flow: advance to next step if on finish tooltip step 3
    if (state.isPractice) {
      setPracticeStep(practiceStep + 1);
      if (state.currentQuestion === state.session.length - 1) {
        navigateThenRun(null, `/preassessment?candidate=${token ?? ""}&step=2`);
        return;
      } else if (nextClick) nextClick();
      return;
    }
    if (state.isAssessment) {
      if (state.egressId) {
        await stopRecording(state.egressId);
        reducers.setEgressId("");
      }
      if(state.currentQuestion < state.session.length - 1)
      {
        if (nextClick) {
          UpdateAssessmentNextQuestion(token||"", state.currentQuestion);
          nextClick();
        }
      }
      else{
        navigateThenRun(()=>SubmitAssessment(token||"", state.currentQuestion, state.session), `/assessmentComplete`); //do things
      }
      return;
    }

    if (setShowLoader) {
      setShowLoader(true);
    }
    getFeedbackAsync();
    setshowFeedback(true);
    setshowNextConfirmation(false);
  };

  if (!micOpts) return <></>;

  // Helper for animation
  const bounceGlowAnim = state.isPractice ? "animate-bounce shadow-[0_0_10px_3px_rgba(255,165,0,0.8)]" : "";

  // Helper for which button is active
  const isActive = (btn: "video" | "mic" | "finish") => {
    if (!state.isPractice) return true;
    if (btn === "video" && practiceStep === 0) return true;
    if (btn === "mic" && (practiceStep === 1 || practiceStep === 2)) return true;
    if (btn === "finish" && practiceStep >= 3) return true;
    return false;
  };

  return (
    <div className="relative">
      {/* Light gray overlay for practice mode */}
      {state.isPractice && (
        <div className="fixed inset-0 bg-gray-300 bg-opacity-70 z-40"></div>
      )}
      {/* Tooltip in the vertical middle of the right half of the screen */}
      {state.isPractice && practiceStep >=0  && (
        <div className="fixed inset-0 flex items-center justify-end z-50 pointer-events-none">
          <div className={`w-1/2   flex items-center ${practiceStep < 3?  "justify-start ml-5": "justify-end mr-3"}`}>
            <CustomTooltip  arrowDirection={practiceStep < 3 ? "down" : "down-right"} className="w-1/2">
              <div className="text-black text-lg font-semibold">
                {practiceStep === 0 && "Click to minimize your camera. Note this doesn't turn your video stream off."}
                {practiceStep === 1 && "Click to test your mic and start speaking."}
                {practiceStep === 2 && "Click once you're done speaking to get a response."}
                {practiceStep === 3 && state.currentQuestion == 0 && "Click to submit your interview responses and continue to the next question. You cannot go back once you submit a question."}
                {practiceStep === 4 && "Click to Finish Your Practie Session."}
              </div>
            </CustomTooltip>
          </div>
        </div>
      )}
      <div className="flex justify-between py-[30px] px-[24px] border-t bg-white shadow-md relative z-50">
        {/* Mic and Video Buttons */}
        <div className="flex gap-2 justify-center items-center">

          {/* Mic Button */}
          {!state.endInterview && micOpts.micListening &&<div className=" z-[60]">
            <button
              type="button"
              role="button"
              title={"mute to send response" }
              className={clsx(
                "rounded-full cursor-pointer text-black bg-[#EAECF5] size-[50px] flex justify-center items-center mt-2 transition-all",
                isActive("mic") && bounceGlowAnim
              )}
              onClick={handleSubmit}
              disabled={!isActive("mic")}
              tabIndex={!isActive("mic") ? -1 : 0}
            >
              { <Mic /> }
            </button>
          </div>}
          {!state.endInterview && !micOpts.micListening && !micOpts.processing && <div className=" z-[60]">
            <button
              type="button"
              role="button"
              title={"unmute to start"}
              className={clsx(
                "rounded-full cursor-pointer text-black bg-[#EAECF5] size-[50px] flex justify-center items-center mt-2 transition-all",
                isActive("mic") && bounceGlowAnim
              )}
              onClick={handleMicClick}
              disabled={!isActive("mic")}
              tabIndex={!isActive("mic") ? -1 : 0}
            >
              { <MicOff />}
            </button>
          </div>}
                    {/* Video Button */}
          {(state.isAssessment || state.isPractice) && (
            <div className="z-[60]">
              <button
                type="button"
                role="button"
                title={"Hide Video the camera does not turn off"}
                className={clsx(
                  "rounded-full text-black bg-[#EAECF5] mouse-pointer size-[50px] flex justify-center items-center mt-2 transition-all",
                  isActive("video") && bounceGlowAnim
                )}
                onClick={handleVideoClick}
                disabled={!isActive("video")}
                tabIndex={!isActive("video") ? -1 : 0}
              >
                {state.showVideo ? <Video /> : <VideoOff />}
              </button>
            </div>
          )}

          {/* Listening indicator */}
          {micOpts.micListening && recognizer && "Listening"}
        </div>
        {/* Finish Button */}

          <div className="flex gap-2 z-[60]">
             {!micOpts.micListening && !micOpts.processing && (
            <button
            
              type="button"
              className={clsx(
                "mt-2 transition-all flex bg-[#F28622] text-black text-sm justify-center border items-center cursor-pointer p-2 rounded-lg font-[500px] hover:bg-gray-100",
                isActive("finish") 
              && bounceGlowAnim) }
              onClick={handleFinish}
              disabled={!isActive("finish")}
              tabIndex={!isActive("finish") ? -1 : 0}
            >
               <p className="p-0 m-0">{state.isPractice && state.currentQuestion === state.session.length - 1 ? "Finish Practice Mode" : "Finish"}</p>
            </button>)
}
          </div>
      </div>
    </div>
  );
};

export default InterviewButtons;
